import { useState, useMemo, useCallback, type CSSProperties } from 'react';
import { Button } from '@helios/ui/button';
import { Modal } from '@helios/ui/overlay';
import { NavIcon, SidebarToggleButton, useBrandTokens } from '../../shell/shared';
import { useAppointmentsContext } from '../../shell/AppointmentsShellLayout';
import { usePowerSyncQuery, usePowerSyncDb } from '../../adapters/powersync';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qAppointmentServicesWithCategory,
  mapServiceRowToUI,
  type AppointmentServiceWithCategoryRow,
  qAppointmentCategories,
  type AppointmentCategoryRow,
} from '@helios/data-access';
import { ServiceDrawer } from './ServiceDrawer';

// ─── Types ───

export interface Service {
  id: string;
  name: string;
  categoryId: string | null;
  categoryName: string;
  categoryColorHex: string;
  description: string;
  duration: number;
  priceCents: number | null;
  active: boolean;
}

// ─── Category Badge ───

function CategoryBadge({ name, colorHex }: { name: string; colorHex: string }) {
  // Derive light bg from hex color
  const bg = colorHex + '22'; // 13% opacity
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        padding: '0 var(--sp-2)',
        borderRadius: 'var(--radius-full)',
        background: bg,
        color: colorHex,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        whiteSpace: 'nowrap',
      }}
    >
      {name || '—'}
    </span>
  );
}

// ─── Toggle ───

function Toggle({ checked, onChange }: { checked: boolean; onChange: (val: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 'var(--radius-full)',
        background: checked ? 'var(--color-primary)' : 'var(--border)',
        border: 'none',
        padding: 2,
        cursor: 'pointer',
        transition: 'background 150ms ease',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 'var(--radius-full)',
          background: '#ffffff',
          transform: checked ? 'translateX(20px)' : 'translateX(0)',
          transition: 'transform 150ms ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        }}
      />
    </button>
  );
}

// ─── Format price ───

// ─── Services Page ───

export function ServicesPage() {
  const brandTokens = useBrandTokens();
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const fmt = useFormatMoney();
  const { collapsed, onToggleSidebar } = useAppointmentsContext();

  const { data: serviceRows = [] } = usePowerSyncQuery<AppointmentServiceWithCategoryRow>(
    qAppointmentServicesWithCategory(),
  );
  const { data: categoryRows = [] } =
    usePowerSyncQuery<AppointmentCategoryRow>(qAppointmentCategories());

  const services: Service[] = useMemo(() => serviceRows.map(mapServiceRowToUI), [serviceRows]);

  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Service | null>(null);

  // Filtered services
  const filtered = useMemo(() => {
    if (!search.trim()) return services;
    const q = search.toLowerCase();
    return services.filter(
      (s) => s.name.toLowerCase().includes(q) || s.categoryName.toLowerCase().includes(q),
    );
  }, [services, search]);

  // Handlers
  const handleNewService = () => {
    setEditingService(null);
    setDrawerOpen(true);
  };

  const handleEditService = (service: Service) => {
    setEditingService(service);
    setDrawerOpen(true);
  };

  const handleSaveService = useCallback(
    async (data: {
      name: string;
      categoryId: string | null;
      description: string;
      duration: number;
      priceCents: number | null;
      active: boolean;
    }) => {
      if (!db) return;
      const now = new Date().toISOString();

      if (editingService) {
        // Update existing
        await db.execute(
          `UPDATE appointment_services SET
            name = ?, category_id = ?, description = ?,
            typical_duration_mins = ?, price_cents = ?, active = ?,
            updated_at = ?
          WHERE id = ?`,
          [
            data.name,
            data.categoryId,
            data.description || null,
            data.duration,
            data.priceCents,
            data.active ? 1 : 0,
            now,
            editingService.id,
          ],
        );
      } else {
        // Insert new
        const id = crypto.randomUUID();
        await db.execute(
          `INSERT INTO appointment_services
            (id, org_id, clinic_id, name, category_id, description,
             typical_duration_mins, price_cents, active, created_at, updated_at)
          VALUES (?, ?, NULL, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            orgId,
            data.name,
            data.categoryId,
            data.description || null,
            data.duration,
            data.priceCents,
            data.active ? 1 : 0,
            now,
            now,
          ],
        );
      }
      setDrawerOpen(false);
      setEditingService(null);
    },
    [db, editingService, orgId],
  );

  const handleToggleActive = useCallback(
    async (service: Service) => {
      if (!db) return;
      await db.execute(`UPDATE appointment_services SET active = ?, updated_at = ? WHERE id = ?`, [
        service.active ? 0 : 1,
        new Date().toISOString(),
        service.id,
      ]);
    },
    [db],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!db || !deleteTarget) return;
    await db.execute(`DELETE FROM appointment_services WHERE id = ?`, [deleteTarget.id]);
    setDeleteTarget(null);
  }, [db, deleteTarget]);

  // Grid columns
  const gridCols = '1fr 120px 100px 100px 70px 80px';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Page Header */}
      <div
        style={{
          height: 'var(--clinic-header-height)',
          padding: '0 var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          {onToggleSidebar && (
            <SidebarToggleButton collapsed={collapsed} onToggle={onToggleSidebar} />
          )}
          <div>
            <h1
              style={{
                fontSize: 'var(--font-size-xl2)',
                fontWeight: 'var(--font-weight-semi)',
                lineHeight: 'var(--line-height-xl2)',
                letterSpacing: '-0.44px',
                color: 'var(--text)',
                margin: 0,
              }}
            >
              Services
            </h1>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                margin: '2px 0 0',
              }}
            >
              Manage the clinic's service catalog used for booking and appointments
            </p>
          </div>
        </div>

        <Button variant="gradient" size="md" onClick={handleNewService}>
          + New Service
        </Button>
      </div>

      {/* Search Bar */}
      <div
        style={{
          padding: 'var(--sp-4) var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            flex: 1,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: 12,
              color: 'var(--text-muted)',
              display: 'flex',
              pointerEvents: 'none',
            }}
          >
            <NavIcon name="search" />
          </div>
          <input
            type="text"
            placeholder="Search service name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              height: 40,
              padding: '0 var(--sp-4) 0 40px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              fontSize: 'var(--font-size-base)',
              color: 'var(--text)',
              outline: 'none',
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 var(--sp-6)' }}>
        {/* Table Header */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: gridCols,
            gap: 'var(--sp-3)',
            alignItems: 'center',
            padding: 'var(--sp-3) var(--sp-4)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <span style={thStyle}>SERVICE</span>
          <span style={thStyle}>CATEGORY</span>
          <span style={thStyle}>DURATION</span>
          <span style={thStyle}>PRICE</span>
          <span style={thStyle}>ACTIVE</span>
          <span style={thStyle}>ACTIONS</span>
        </div>

        {/* Table Rows */}
        {filtered.map((service) => (
          <div
            key={service.id}
            style={{
              display: 'grid',
              gridTemplateColumns: gridCols,
              gap: 'var(--sp-3)',
              alignItems: 'center',
              padding: 'var(--sp-3) var(--sp-4)',
              borderBottom: '1px solid var(--border)',
              transition: 'background 100ms ease',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLDivElement).style.background =
                'var(--bg-hover, rgba(0,0,0,0.02))';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLDivElement).style.background = 'transparent';
            }}
          >
            {/* Service Name */}
            <span
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text)',
              }}
            >
              {service.name}
            </span>

            {/* Category Badge */}
            <CategoryBadge name={service.categoryName} colorHex={service.categoryColorHex} />

            {/* Duration */}
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
              }}
            >
              {service.duration} min
            </span>

            {/* Price */}
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                color: service.priceCents != null ? 'var(--text)' : 'var(--text-muted)',
                fontWeight: 'var(--font-weight-medium)',
              }}
            >
              {service.priceCents == null ? '—' : fmt(service.priceCents / 100)}
            </span>

            {/* Active Toggle */}
            <Toggle checked={service.active} onChange={() => handleToggleActive(service)} />

            {/* Actions */}
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              <button
                onClick={() => handleEditService(service)}
                aria-label="Edit service"
                style={actionBtnStyle}
              >
                <NavIcon name="edit" />
              </button>
              <button
                onClick={() => setDeleteTarget(service)}
                aria-label="Delete service"
                style={{ ...actionBtnStyle, color: 'var(--color-danger)' }}
              >
                <NavIcon name="trash" />
              </button>
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div
            style={{
              padding: 'var(--sp-12) var(--sp-6)',
              textAlign: 'center',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-base)',
            }}
          >
            No services found matching your search.
          </div>
        )}
      </div>

      {/* Service Drawer */}
      <ServiceDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        brandTokens={brandTokens}
        service={editingService}
        categories={categoryRows}
        onSave={handleSaveService}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete Service"
        maxWidth={420}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-3)' }}>
            <Button variant="outline" size="md" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="solid"
              size="md"
              onClick={handleDeleteConfirm}
              style={{ background: 'var(--color-danger)', color: '#fff' }}
            >
              Delete
            </Button>
          </div>
        }
      >
        <div style={{ padding: 'var(--sp-6, 24px)' }}>
          <p style={{ color: 'var(--text)', fontSize: 'var(--font-size-base)', margin: 0 }}>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This action
            cannot be undone.
          </p>
        </div>
      </Modal>
    </div>
  );
}

// ─── Shared styles ───

const thStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const actionBtnStyle: CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 'var(--radius-md)',
  background: 'transparent',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  padding: 0,
};
